import { useEffect } from 'react';

/** The height the chat surfaces size themselves against. */
export const APP_VIEWPORT_HEIGHT_VAR = '--app-vh';

/**
 * How far the visible viewport has been panned down inside the layout one.
 *
 * Only a `position: fixed` box needs this. Fixed is resolved against the
 * LAYOUT viewport, which on iOS does not move or shrink when the keyboard
 * opens - so a panel pinned to `bottom: 0` is pinned behind the keyboard, and
 * scrolling to reach it just pans it back down. Offsetting the top by this and
 * sizing by the height above puts it back on the part of the screen the user
 * can actually see.
 */
export const APP_VIEWPORT_TOP_VAR = '--app-vv-top';

/**
 * The vertical centre of the visible viewport, in layout-viewport pixels:
 * top offset above plus half the height above.
 *
 * Published for the modals. A modal is `position: fixed; top: 50%`, which is
 * the centre of the LAYOUT viewport - with the keyboard up that point sits
 * behind the keys, and the half of the modal holding the focused field with
 * it. Centring on this variable instead keeps the box on the visible part of
 * the screen; where the hook is not mounted the variable is absent and the
 * modal's own `50%` fallback applies.
 */
export const APP_VIEWPORT_CENTER_VAR = '--app-vv-center';

/** Where the layout stops being a page and becomes a single full-screen card. */
const MOBILE_BREAKPOINT = 800;

/**
 * Publishes the *visible* viewport height as a CSS variable, and stops the page
 * behind the chat from scrolling on a phone.
 *
 * ## The height
 *
 * Every unit CSS offers measures the wrong thing for a full-height chat on a
 * phone:
 *
 *   - `100vh` is the tallest the viewport ever gets, so on iOS Safari the
 *     bottom of the page sits behind the URL bar - the composer is simply not
 *     on screen until you scroll.
 *   - `100dvh` fixes that, and is what these screens fall back to. But it
 *     tracks the *layout* viewport, and on iOS the layout viewport does NOT
 *     shrink when the keyboard opens. Tap the composer and the keyboard covers
 *     the very thing you are typing into.
 *   - `100svh` is stable but permanently short, wasting a URL bar's worth of
 *     conversation whenever the bar is collapsed.
 *
 * `visualViewport.height` is the one number that means "what the user can
 * actually see right now": it accounts for the collapsing browser chrome AND
 * for the keyboard, on both iOS and Android.
 *
 * ## The scroll lock
 *
 * Sizing the card correctly is not enough on its own. `ContentStyled` is
 * `min-height: 100vh`, so the document stays a full TALL viewport high while
 * the keyboard has cut the visible area to about half of it - which leaves the
 * page scrollable by hundreds of pixels. iOS then pans the layout viewport to
 * reveal the focused input, and a flick while typing scrolls the card off
 * screen entirely, leaving nothing but background.
 *
 * So on a phone the body is pinned while the chat is mounted. `position: fixed`
 * rather than `overflow: hidden`, which iOS Safari has always ignored on the
 * body. `overflow` is deliberately left alone: `useDisableBodyScroll` writes to
 * it whenever a modal opens over the chat, and its `unset` on close would
 * quietly undo half a lock that used the same property.
 *
 * The alternative for the height - `interactive-widget=resizes-content` in the
 * viewport meta - is deliberately not used: Safari ignores it, and it changes
 * how the keyboard affects EVERY page in the app, not just this one.
 *
 * Mounted by the chat pages rather than app-wide, and everything it sets is
 * undone on unmount.
 */
export const useVisualViewportHeight = () => {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const media = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

    const lockBody = () => {
      const { style } = document.body;
      if (style.position === 'fixed') return;
      // Land at the top before pinning, or the page freezes wherever iOS had
      // already panned it to.
      window.scrollTo(0, 0);
      style.position = 'fixed';
      style.top = '0';
      style.left = '0';
      style.right = '0';
    };

    const unlockBody = () => {
      const { style } = document.body;
      if (style.position !== 'fixed') return;
      style.position = '';
      style.top = '';
      style.left = '';
      style.right = '';
    };

    const apply = () => {
      /*
       * Never taller than the window it is laid out in.
       *
       * The visual and the layout viewport are normally the same height on a
       * desktop, but they drift: a window resize the visual viewport did not
       * report (a background tab, a devtools dock), or an iOS URL bar caught
       * part way through collapsing, leaves `visualViewport.height` bigger than
       * the page. A card sized against that runs off the bottom of the screen,
       * and the symptom is not a card slightly too long - it is that the
       * document starts scrolling behind a conversation that is already
       * scrolling itself, and the composer ends up below the fold with the last
       * message tucked underneath it and no way left to scroll to it.
       *
       * Clamping costs nothing in the case this hook exists for: with a
       * keyboard up the visual viewport is the SMALLER of the two, so it still
       * wins.
       */
      const visible = Math.min(
        viewport.height,
        document.documentElement.clientHeight || viewport.height
      );

      document.documentElement.style.setProperty(
        APP_VIEWPORT_HEIGHT_VAR,
        `${visible}px`
      );

      /* Negative values happen mid-gesture on iOS (rubber-banding past the top)
         and would push a fixed panel off the screen. */
      const top = Math.max(0, viewport.offsetTop);
      document.documentElement.style.setProperty(
        APP_VIEWPORT_TOP_VAR,
        `${top}px`
      );

      document.documentElement.style.setProperty(
        APP_VIEWPORT_CENTER_VAR,
        `${top + visible / 2}px`
      );

      if (!media.matches) {
        unlockBody();
        return;
      }

      lockBody();
      /*
       * Belt and braces. iOS pans to reveal a focused input BEFORE the layout
       * has been re-sized against the new visible height, and it can get that
       * pan in ahead of the lock on the very first focus of a session.
       */
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };

    apply();
    viewport.addEventListener('resize', apply);
    /*
     * `scroll` as well: on iOS the visual viewport can be panned inside the
     * layout viewport while the keyboard is up, and that changes what is
     * visible without ever firing a resize.
     */
    viewport.addEventListener('scroll', apply);
    /*
     * And the window's own resize, which is the event that fires when the two
     * viewports drift apart - the layout one has changed and the visual one may
     * not have said so. `apply` reads both and is idempotent, so the overlap
     * with the listeners above costs a property write.
     */
    window.addEventListener('resize', apply);
    media.addEventListener('change', apply);

    return () => {
      viewport.removeEventListener('resize', apply);
      viewport.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      media.removeEventListener('change', apply);
      unlockBody();
      document.documentElement.style.removeProperty(APP_VIEWPORT_HEIGHT_VAR);
      document.documentElement.style.removeProperty(APP_VIEWPORT_TOP_VAR);
      document.documentElement.style.removeProperty(APP_VIEWPORT_CENTER_VAR);
    };
  }, []);
};
